import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Wallet,
  Bitcoin,
  ArrowUpRight,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Info,
  History,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletSettings = () => {
  const { user, refreshUser } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');

  const fetchWalletData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wallet`, { withCredentials: true });
      setWalletData(response.data);
      setWalletAddress(response.data.btc_wallet_address || '');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch wallet data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  const saveWalletAddress = async (e) => {
    e.preventDefault();
    if (!walletAddress.trim()) {
      toast.error('Please enter a Bitcoin wallet address');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/wallet`,
        { btc_wallet_address: walletAddress },
        { withCredentials: true }
      );
      toast.success('Wallet address saved!');
      await refreshUser();
      await fetchWalletData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save wallet address';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const requestWithdrawal = async () => {
    if (!walletData?.btc_wallet_address) {
      toast.error('Please set your wallet address first');
      return;
    }

    if (user?.balance_satoshis < walletData?.minimum_withdrawal) {
      toast.error(`Minimum withdrawal is ${walletData?.minimum_withdrawal} satoshis`);
      return;
    }

    setWithdrawing(true);
    try {
      const response = await axios.post(`${API}/wallet/withdraw`, {}, { withCredentials: true });
      toast.success(`Withdrawal of ${response.data.amount_satoshis} satoshis requested!`);
      await refreshUser();
      await fetchWalletData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to request withdrawal';
      toast.error(message);
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading wallet...</p>
        </div>
      </div>
    );
  }

  const canWithdraw =
    walletData?.btc_wallet_address && user?.balance_satoshis >= walletData?.minimum_withdrawal;
  const progressPercentage = Math.min(
    100,
    ((user?.balance_satoshis || 0) / (walletData?.minimum_withdrawal || 10000)) * 100
  );

  return (
    <div className="min-h-screen pt-20 pb-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#F7931A]/10 border border-[#F7931A]/30 mb-6">
            <Wallet className="w-10 h-10 text-[#F7931A]" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Wallet <span className="text-gradient-gold">Settings</span>
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto mb-6">
            Configure your Bitcoin wallet address for withdrawals
          </p>
          <Link to="/wallet/history">
            <Button
              variant="outline"
              className="border-white/20 hover:bg-white/5"
              data-testid="view-withdrawal-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              View Withdrawal History
            </Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Balance Card */}
          <div className="card-dark p-5 sm:p-8 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-semibold">Current Balance</h2>
              <Bitcoin className="w-8 h-8 text-[#F7931A]/30 flex-shrink-0" />
            </div>

            <div className="mb-6">
              <div className="font-mono text-3xl sm:text-4xl font-bold text-[#F7931A] mb-2 break-words">
                {user?.balance_satoshis?.toLocaleString() || 0}
                <span className="text-lg text-slate-400 ml-2">sats</span>
              </div>
              <p className="text-slate-400">
                ≈ ${((user?.balance_satoshis || 0) * 0.0005).toFixed(4)} USD
              </p>
            </div>

            {/* Progress to minimum */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs sm:text-sm mb-2 gap-2 flex-wrap">
                <span className="text-slate-400">Progress to withdrawal</span>
                <span className="text-slate-300 font-mono">
                  {user?.balance_satoshis?.toLocaleString() || 0} /{' '}
                  {walletData?.minimum_withdrawal?.toLocaleString()} sats
                </span>
              </div>
              <div className="h-3 bg-[#0D0F17] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#F7931A] to-[#FFD700] rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            <Button
              onClick={requestWithdrawal}
              disabled={!canWithdraw || withdrawing}
              className={`w-full py-5 ${
                canWithdraw ? 'btn-gold' : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }`}
              data-testid="withdraw-btc-btn"
            >
              {withdrawing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <ArrowUpRight className="w-5 h-5 mr-2" />
                  Request Withdrawal
                </>
              )}
            </Button>

            {!canWithdraw && (
              <div className="mt-4 flex items-start gap-2 text-sm text-slate-400">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {!walletData?.btc_wallet_address
                    ? 'Please set your wallet address first'
                    : `Minimum withdrawal is ${walletData?.minimum_withdrawal?.toLocaleString()} satoshis`}
                </span>
              </div>
            )}
          </div>

          {/* Wallet Address Card */}
          <div className="card-dark p-5 sm:p-8 min-w-0">
            <h2 className="font-heading text-xl font-semibold mb-6">Bitcoin Wallet Address</h2>

            <form onSubmit={saveWalletAddress} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wallet" className="text-slate-300">
                  Your BTC Address
                </Label>
                <Input
                  id="wallet"
                  type="text"
                  placeholder="bc1q... or 1... or 3..."
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  className="bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50 font-mono text-sm"
                  data-testid="wallet-address-input"
                />
                <p className="text-xs text-slate-500">
                  Supports Legacy (1...), SegWit (3...), and Native SegWit (bc1...) addresses
                </p>
              </div>

              {walletData?.btc_wallet_address && (
                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg min-w-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div className="text-sm min-w-0 flex-1">
                    <p className="text-emerald-400 font-medium">Wallet Connected</p>
                    <p className="text-slate-400 font-mono text-xs truncate">
                      {walletData.btc_wallet_address}
                    </p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full btn-gold py-5"
                disabled={saving}
                data-testid="save-wallet-btn"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Wallet className="w-5 h-5 mr-2" />
                    Save Wallet Address
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 card-dark p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-[#F7931A] flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-2">Withdrawal Information</h3>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Minimum withdrawal: {walletData?.minimum_withdrawal?.toLocaleString()} satoshis</li>
                <li>• Withdrawals are processed manually within 24-48 hours</li>
                <li>• Please double-check your wallet address before saving</li>
                <li>• Network fees will be deducted from the withdrawal amount</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletSettings;
